/**
 * Helper: Format Nosto API
 * -----------------------------------------------------------------------------
 * Formats values and objects.
 *
 */
/* eslint-disable no-constant-binary-expression */
import { formatMoney } from '@shopify/theme-currency'

import { formatPrice } from '~/helpers/format'
import { decodeApiId } from '~/helpers/graphql'

import availableForSaleByHandle from '~/graphql/queries/product-available.gql'

/**
 * Format Nosto API product.
 * @param {Object} product - Nosto formatted product.
 * @returns {Object}
 */
export function formatNostoProduct(product) {
  const handle = product.url.split('/products/')[1]
  const tags1 = product.tags1 || []
  const tags2 = product.tags2 || []
  const tags3 = product.tags3 || []

  const formattedProduct = {
    available: product.skus?.length
      ? product.skus?.some((sku) => sku.availability === 'InStock')
      : product.available,
    compareAtPrice: formatNostoPrice(product.skus, 'listPrice', product.priceCurrencyCode, product.listPrice),
    description: product.description,
    handle,
    id: Number(product.productId),
    images: formatNostoImages(product),
    options: formatNostoOptions(product.skus),
    price: formatNostoPrice(product.skus, 'price', product.priceCurrencyCode, product.price),
    tags: [...tags1, ...tags2, ...tags3],
    title: product.name,
    type: product.customFields.productType || '',
    url: `//${location.host}${cnvs.routes.normalisedRoot}products/${handle}`,
    vendor: product.brand,
  }

  /**
   * Add additional values which rely on formatted response.
   */
  formattedProduct.featuredImage = formattedProduct.images[0]
  formattedProduct.giftCard = formattedProduct.type === 'Gift Cards'
  formattedProduct.variants = formatNostoVariants(product, formattedProduct.giftCard)

  formattedProduct.hasOnlyDefaultVariant = formattedProduct.variants?.length
    ? formattedProduct.variants.every((variant) => {
      return variant.title.trim().toLowerCase() === 'default title'
    })
    : true

  /**
   * Product badges.
   */
  formattedProduct.badges = formattedProduct.tags.map((tag) => {
    if (!tag.includes('Badge:')) {
      return false
    }

    return tag.split('Badge:')[1].trim()
  }).filter(Boolean)

  /**
   * Format metafields.
   * - Add each metafield to object, then add case.
   */
  const metafields = {
    colours: false,
    custom_id: '',
    vendor_link: '',
    product_title: product.name,
  }

  let coloursLoaded = true

  for (const key of Object.keys(metafields)) {
    const object = product.customFields?.find((metafield) => {
      const metaKey = metafield.key.split('-')[1]
      return metaKey === key
    })

    if (!object) {
      continue
    }

    let value = object.value
    let formattedKey = key

    switch (key) {
      case 'colours':
        value = JSON.parse(object.value)
        coloursLoaded = false
        break

      case 'custom_id':
        formattedKey = 'customId'
        delete metafields[key]
        break

      case 'product_title':
        value = object.value ? object.value : product.name
        break

      case 'vendor_link':
        value = value.replace(
          'https://childsplayredirect.com/',
          `//${location.host}${cnvs.routes.normalisedRoot}`,
        )

        formattedKey = 'vendorLink'
        delete metafields[key]
        break
    }

    metafields[formattedKey] = value
  }

  return {
    ...formattedProduct,
    ...metafields,
    coloursLoaded,
  }
}

/**
 * Format Nosto API product prices.
 * @param {Array} variants - Nosto format variants.
 * @param {String} property - Property to look at.
 * @param {String} currencyCode - Nosto currency code.
 * @param {Number} amount - Fallback amount.
 * @return {Object}
 */
function formatNostoPrice(variants, property, currencyCode, amount) {
  if (!variants?.length) {
    return {
      amount,
      currencyCode,
      max: false,
      min: false,
      varies: false,
    }
  }

  const prices = variants
    .map((variant) => variant[property])
    .sort()

  const max = isNaN(prices[prices.length - 1])
    ? false
    : Number(prices[prices.length - 1]) * 100

  const min = isNaN(prices[0])
    ? false
    : Number(prices[0]) * 100

  return {
    amount: min,
    currencyCode,
    max,
    min,
    varies: max !== min,
  }
}

/**
 * Format Nosto API images.
 * @param {Object} product - Nosto formatted product.
 * @returns {Array}
 */
function formatNostoImages(product) {
  let images = [product.imageUrl]

  if (product.alternateImageUrls) {
    images = [
      product.imageUrl,
      ...product.alternateImageUrls,
    ]
  }

  return images.map((src, id) => {
    return {
      alt: '',
      height: 0,
      id,
      src,
      variant: false,
      width: 0,
    }
  })
}

/**
 * Format Nosto API options.
 * @param {Array} variants - Nosto format variants.
 * @returns {Array}
 */
function formatNostoOptions(variants) {
  if (!variants?.length) {
    return []
  }

  const options = {}

  variants.forEach((variant) => {
    variant.customFields?.forEach((field) => {
      if (field.key === 'colour' || field.key === 'size') {
        if (!options[field.key]) {
          options[field.key] = [field.value]
          return
        }

        options[field.key].push(field.value)
      }
    })
  })

  return Object.entries(options).map(([name, values], id) => {
    const duplicates = []

    const formattedValues = values.filter((value) => {
      if (duplicates.includes(value)) {
        return false
      }

      duplicates.push(value)
      return true
    })

    return {
      id,
      name,
      values: formattedValues,
    }
  })
}

/**
 * Format Nosto API product variants.
 * @param {Object} product - Nosto formatted product.
 * @param {Boolean} isGiftCard - Product type is gift card.
 * @returns {Array}
 */
function formatNostoVariants(product, isGiftCard) {
  return product.skus?.map((variant) => {
    const formattedVariant = {
      available: variant.availability === 'InStock',
      compareAtPrice: {
        amount: isNaN(variant.listPrice)
          ? false
          : Number(variant.listPrice) * 100,
        currencyCode: product.priceCurrencyCode,
      },
      id: Number(variant.id),
      image: false,
      inventory: 100,
      price: {
        amount: Number(variant.price) * 100 ?? false,
        currencyCode: product.priceCurrencyCode,
      },
      selectedOptions: variant.customFields?.filter(field => field.key === 'colour' || field.key === 'size')
        .map((field) => {
          return {
            name: field.key,
            value: field.value,
          }
        }),
    }

    /**
     * Wait for prices to be formatted before determining title.
     * - Gift cards show the price as their title for multi-currency purposes.
     */
    formattedVariant.title = isGiftCard
      ? formatMoney(formattedVariant.price.amount, cnvs.store.moneyFormat)
      : variant.name

    return formattedVariant
  })
}

/**
 * Format prices data from Shopify.
 * @param {Object} response - GraphQL response.
 * @returns {Object}
 */
export function formatShopifyPrices(response) {
  const product = response.node

  return {
    id: decodeApiId(product.id),
    price: formatPrice(product.priceRange),
    compareAtPrice: formatPrice(product.compareAtPriceRange),
  }
}

/**
 * Filter available colours from a colour object by checking product
 * availability via GraphQL.
 * - Used in quick-view.vue.
 * @param {Object} colourObject - Object mapping names to product handles.
 * @returns {Promise<Object|null>} - Object of available colours and their
 * handles, or null if there is one or fewer entries.
 */
export async function filterAvailableColours(colourObject) {
  const colourEntries = Object.entries(colourObject)

  if (colourEntries.length <= 1) {
    return null
  }

  const entries = await Promise.all(
    colourEntries.map(async([colour, colourHandle]) => {
      const data = await cnvs.Query({
        query: availableForSaleByHandle,
        variables: {
          country: cnvs.store.country,
          handle: colourHandle,
          language: cnvs.store.language,
        },
      })

      if (data?.product?.availableForSale) {
        return [colour, colourHandle]
      }

      return null
    }),
  )

  return Object.fromEntries(entries.filter(Boolean))
}
